import { COMPANY_SUFFIXES } from './constants';

/**
 * Generates a URL-friendly slug from an organization name
 *
 * Process:
 * 1. Removes common company suffixes (Inc, LLC, Corp, etc.)
 * 2. Converts to lowercase
 * 3. Replaces spaces and special characters with hyphens
 * 4. Removes consecutive hyphens
 * 5. Trims leading/trailing hyphens
 *
 * @param organizationName - The organization name to convert
 * @returns A URL-friendly slug
 *
 * @example
 * generateWorkspaceSlug('Salesforce Inc') // 'salesforce'
 * generateWorkspaceSlug('Acme Corp.') // 'acme'
 * generateWorkspaceSlug('Big Tech Company LLC') // 'big-tech-company'
 */
export function generateWorkspaceSlug(organizationName: string): string {
  let slug = organizationName.trim();

  // Remove company suffixes (case-insensitive)
  for (const suffix of COMPANY_SUFFIXES) {
    const regex = new RegExp(`\\b${suffix}\\b\\.?`, 'gi');
    slug = slug.replace(regex, '');
  }

  // Convert to lowercase and replace spaces/special chars with hyphens
  slug = slug
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
    .replace(/-+/g, '-') // Replace consecutive hyphens with single hyphen
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens

  return slug;
}

/**
 * Generates a workspace email address from a slug
 *
 * @param slug - The workspace slug
 * @returns Email address in format: {slug}@invites.lingaai.com
 *
 * @example
 * generateWorkspaceEmail('salesforce') // 'salesforce@invites.lingaai.com'
 */
export function generateWorkspaceEmail(slug: string): string {
  return `${slug}@invites.lingaai.com`;
}
